<script setup lang="ts">
import { QField } from 'quasar'
import dayjs, { Dayjs } from 'dayjs'
import { Color } from '@previo/codebooks'
import { computed, nextTick, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import type { PropType } from 'vue'
import { $t } from '@previo/core'
import { onClickOutside } from '@vueuse/core'
import { DateTimeRange } from '../../date-time-range-picker'
import type { PDateRangePickerFastChoice } from '../../date-range-picker/types'
import PDateTimeRangePicker from '../../date-time-range-picker/PDateTimeRangePicker.vue'
import PBtn from '../../btn/PBtn.vue'
import { PBtnType } from '../../btn/types'
import PCard from '../../card/PCard.vue'
import LabelInputField from '../components/LabelInputField.vue'
import PSpace from '../../space/PSpace.vue'
import { PSpaceSize } from '../../space/types'
import PTextFormBody from '../../typography/form/PTextFormBody.vue'
import { PTimeInputFieldIncrement } from '../types'
import { type PValidationRule } from '../../../types/types'
import { usePrevioDS } from '../../../composables/helpers'
import { menuOffset } from '../composable/menuOffset'
import PIcon from '../../icon/PIcon.vue'
import { useDateTimeRangeValidator, useTimeRangeValidator } from './composables/rules'
import DateTimeContextMenu from './components/date-time-input-field/DateTimeContextMenu.vue'
import DateTimeInputField from './components/date-time-input-field/DateTimeInputField.vue'
import FormatSegmentBuilder from './components/segments/FormatSegmentBuilder.vue'
import { ErrorState } from './components/segments/model/ErrorState'
import { type PPickerPosition, PPickerPositionEnum } from './PPickerPosition'
import { PDateTimeRangeInputFieldLayout } from './PDateTimeRangeInputFieldLayout'

type Emits = {
  (e: 'confirm'): void
}

enum SelectionTarget {
  FROM = 'from',
  TO = 'to',
}

const emits = defineEmits<Emits>()

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  tooltip: {
    type: String,
    default: null,
  },
  labelFrom: {
    type: String,
    default: () => $t('From'),
  },
  tooltipFrom: {
    type: String,
    default: null,
  },
  labelTo: {
    type: String,
    default: () => $t('To'),
  },
  tooltipTo: {
    type: String,
    default: null,
  },
  fastChoices: {
    type: Array as PropType<PDateRangePickerFastChoice[]>,
    default: () => [],
  },
  min: {
    type: Object as PropType<Dayjs>,
    required: false,
  },
  max: {
    type: Object as PropType<Dayjs>,
    required: false,
  },
  maxRangeDays: {
    type: Number,
    required: false,
  },
  timeStrict: {
    type: Boolean,
    default: false,
  },
  timeIncrement: {
    type: Number as PropType<PTimeInputFieldIncrement>,
    default: PTimeInputFieldIncrement.QUARTER,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  disableClearable: {
    type: Boolean,
    default: false,
  },
  hideTimeField: {
    type: Boolean,
    default: false,
  },
  includeTimeValidation: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  defaultTimeFrom: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) {
        return true
      }

      return dayjs(value, 'HH:mm', true).isValid()
    },
    default: '00:00',
  },
  defaultTimeTo: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) {
        return true
      }

      return dayjs(value, 'HH:mm', true).isValid()
    },
    default: '23:59',
  },
  anchorPicker: {
    type: String as PropType<PPickerPosition>,
    default: PPickerPositionEnum.BOTTOM_START,
  },
  selfPicker: {
    type: String as PropType<PPickerPosition>,
    default: PPickerPositionEnum.TOP_START,
  },
  offsetPicker: {
    type: Array as PropType<number[]>,
    default: menuOffset,
  },
  cy: {
    type: String,
    required: true,
  },
  rules: {
    type: Array as PropType<PValidationRule[]>,
    default: () => [],
  },
  rulesFrom: {
    type: Array as PropType<PValidationRule[]>,
    default: () => [],
  },
  rulesTo: {
    type: Array as PropType<PValidationRule[]>,
    default: () => [],
  },
  layout: {
    type: String as PropType<PDateTimeRangeInputFieldLayout>,
    default: PDateTimeRangeInputFieldLayout.AUTO,
  },
  errorMessage: {
    type: String,
    default: null,
  },
  splitFields: {
    type: Boolean,
    default: false,
  },
  hideConfirmButton: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel<DateTimeRange>({ default: new DateTimeRange() })

const ds = usePrevioDS()

const validateRangeRule: PValidationRule = (rangeValue: DateTimeRange | null) => {
  if (!rangeValue?.from || !rangeValue?.to) {
    return true
  }

  if (rangeValue.from.isAfter(rangeValue.to)) {
    return $t('"From" date and time cannot be after "To" date and time')
  }

  return true
}

const isRequired = computed<boolean>(() => {
  return (
    props.rules.includes(ds.rules.required) ||
    props.rulesFrom.includes(ds.rules.required) ||
    props.rulesTo.includes(ds.rules.required)
  )
})

const dateTimeRangePickerComponent = ref<InstanceType<typeof PDateTimeRangePicker>>()
const qFieldComponent = ref<InstanceType<typeof QField>>()
const fromSegmentBuilderComponent = ref<InstanceType<typeof FormatSegmentBuilder>>()
const toSegmentBuilderComponent = ref<InstanceType<typeof FormatSegmentBuilder>>()
const dateTimeInputFieldFromComponent = ref<InstanceType<typeof DateTimeInputField>>()
const dateTimeInputFieldToComponent = ref<InstanceType<typeof DateTimeInputField>>()

const isDateTimePickerVisible = ref<boolean>(false)
const isOpeningPicker = ref<boolean>(false)
const rootRef = ref<HTMLElement>()
const containerRef = ref<HTMLElement>()
const fromErrorState = shallowRef<ErrorState>(new ErrorState())
const toErrorState = shallowRef<ErrorState>(new ErrorState())
const resizeObserver = ref<ResizeObserver | null>(null)
const isHorizontalLayout = ref<boolean>(false)

const contextMenuTitle = ref<string>($t('Selecting from'))
const contextMenuTitleProxy = computed({
  get: () => contextMenuTitle.value,
  set: (value: string) => {
    contextMenuTitle.value = value
  },
})

const fromErrorStateModel = computed<ErrorState>({
  get: () => fromErrorState.value,
  set: (value: ErrorState) => {
    fromErrorState.value = value
  },
})

const toErrorStateModel = computed<ErrorState>({
  get: () => toErrorState.value,
  set: (value: ErrorState) => {
    toErrorState.value = value
  },
})

const ensureModel = (): DateTimeRange => {
  if (!model.value) {
    model.value = new DateTimeRange()
  }

  return model.value
}

const getRangeBoundaryValue = (target: SelectionTarget): Dayjs | null => {
  return model.value?.[target] ?? null
}

const setRangeBoundaryValue = (target: SelectionTarget, value: Dayjs | null): void => {
  const nextRange = ensureModel().clone()
  nextRange[target] = value
  model.value = nextRange
}

const createRangeBoundaryModel = (target: SelectionTarget) => {
  return computed<Dayjs | null>({
    get: () => getRangeBoundaryValue(target),
    set: (value: Dayjs | null) => {
      setRangeBoundaryValue(target, value)
    },
  })
}

const getOppositeSelectionTarget = (target: SelectionTarget): SelectionTarget => {
  return target === SelectionTarget.FROM ? SelectionTarget.TO : SelectionTarget.FROM
}

const getDateTimeInputFieldComponent = (
  target: SelectionTarget,
): InstanceType<typeof DateTimeInputField> | undefined => {
  return target === SelectionTarget.FROM
    ? dateTimeInputFieldFromComponent.value
    : dateTimeInputFieldToComponent.value
}

const getFormatSegmentBuilderComponent = (
  target: SelectionTarget,
): InstanceType<typeof FormatSegmentBuilder> | undefined => {
  return target === SelectionTarget.FROM
    ? fromSegmentBuilderComponent.value
    : toSegmentBuilderComponent.value
}

const getSelectionTitle = (target: SelectionTarget): string => {
  return target === SelectionTarget.FROM ? $t('Selecting from') : $t('Selecting to')
}

const setSplitFieldFocus = (target: SelectionTarget): void => {
  getDateTimeInputFieldComponent(target)?.focus()
  getDateTimeInputFieldComponent(getOppositeSelectionTarget(target))?.unfocus()
}

const setCombinedFieldSelection = (target: SelectionTarget): void => {
  getFormatSegmentBuilderComponent(target)?.select()
  getFormatSegmentBuilderComponent(getOppositeSelectionTarget(target))?.deselect()
}

const setPickerTarget = (target: SelectionTarget): void => {
  if (target === SelectionTarget.FROM) {
    dateTimeRangePickerComponent.value?.setSelectingFrom()
    return
  }

  dateTimeRangePickerComponent.value?.setSelectingTo()
}

const focusPickerTarget = (target: SelectionTarget): void => {
  if (props.splitFields) {
    setSplitFieldFocus(target)
    return
  }

  setCombinedFieldSelection(target)
}

const clearBoundaryValue = (target: SelectionTarget): void => {
  const range = ensureModel()

  if (target === SelectionTarget.FROM) {
    range.clearFrom()
    return
  }

  range.clearTo()
}

const openSplitPicker = (target: SelectionTarget): void => {
  getDateTimeInputFieldComponent(getOppositeSelectionTarget(target))?.unfocus()
  showPicker(target)
}

const unfocusSplitFields = (): void => {
  dateTimeInputFieldFromComponent.value?.unfocus()
  dateTimeInputFieldToComponent.value?.unfocus()
}

const fromBoundaryModel = createRangeBoundaryModel(SelectionTarget.FROM)
const toBoundaryModel = createRangeBoundaryModel(SelectionTarget.TO)

const validateFromDateTimeRange = useDateTimeRangeValidator(
  fromBoundaryModel,
  !props.hideTimeField,
  props.min,
  props.max,
)
const validateToDateTimeRange = useDateTimeRangeValidator(
  toBoundaryModel,
  !props.hideTimeField,
  props.min,
  props.max,
)
const validateFromTimeRange = useTimeRangeValidator(fromBoundaryModel, props.min, props.max)
const validateToTimeRange = useTimeRangeValidator(toBoundaryModel, props.min, props.max)

const mapRangeRule = (rule: PValidationRule): PValidationRule => {
  return (rangeValue: DateTimeRange | null) => {
    const hasCompleteRange = !!rangeValue?.from && !!rangeValue?.to
    const isRequiredRule = rule === ds.rules.required

    if (!hasCompleteRange && !isRequiredRule) {
      return true
    }

    return rule(rangeValue ?? new DateTimeRange())
  }
}

const mapFromRule = (rule: PValidationRule): PValidationRule => {
  return (rangeValue: DateTimeRange | null) => {
    return rule(rangeValue?.from ?? null)
  }
}

const mapToRule = (rule: PValidationRule): PValidationRule => {
  return (rangeValue: DateTimeRange | null) => {
    return rule(rangeValue?.to ?? null)
  }
}

const mapSplitRangeRule = (rule: PValidationRule): PValidationRule => {
  return () => {
    const rangeValue = model.value ?? new DateTimeRange()
    const hasCompleteRange = !!rangeValue.from && !!rangeValue.to
    const isRequiredRule = rule === ds.rules.required

    if (!hasCompleteRange && !isRequiredRule) {
      return true
    }

    return rule(rangeValue)
  }
}

const validateSplitRangeRule: PValidationRule = () => {
  return validateRangeRule(model.value ?? new DateTimeRange())
}

const externalErrorRule: PValidationRule = () => {
  return props.errorMessage || true
}

const qRules = computed<PValidationRule[]>(() => {
  const rules = [
    ...props.rules.map(mapRangeRule),
    ...props.rulesFrom.map(mapFromRule),
    ...props.rulesTo.map(mapToRule),
    validateFromDateTimeRange,
    validateToDateTimeRange,
    validateRangeRule,
  ]

  if (props.includeTimeValidation) {
    rules.push(validateFromTimeRange, validateToTimeRange)
  }

  return rules
})

const qRulesFrom = computed<PValidationRule[]>(() => {
  return [
    ...props.rules.map(mapSplitRangeRule),
    ...props.rulesFrom,
    externalErrorRule,
    validateSplitRangeRule,
  ]
})

const qRulesTo = computed<PValidationRule[]>(() => {
  return [
    ...props.rules.map(mapSplitRangeRule),
    ...props.rulesTo,
    externalErrorRule,
    validateSplitRangeRule,
  ]
})

const isHorizontalLayoutResolved = computed<boolean>(() => {
  if (props.layout === PDateTimeRangeInputFieldLayout.HORIZONTAL) {
    return true
  }

  if (props.layout === PDateTimeRangeInputFieldLayout.VERTICAL) {
    return false
  }

  return isHorizontalLayout.value
})

const observeContainer = (element?: HTMLElement): void => {
  if (!resizeObserver.value || !element) {
    return
  }

  resizeObserver.value.observe(element)
}

const unobserveContainer = (element?: HTMLElement): void => {
  if (!resizeObserver.value || !element) {
    return
  }

  resizeObserver.value.unobserve(element)
}

const getCombinedFieldElement = (): HTMLElement | undefined => {
  return qFieldComponent.value?.$el as HTMLElement | undefined
}

const attachCombinedFieldListener = (): void => {
  if (props.splitFields) {
    return
  }

  getCombinedFieldElement()?.addEventListener('click', onOpenCombinedPicker, true)
}

const detachCombinedFieldListener = (): void => {
  getCombinedFieldElement()?.removeEventListener('click', onOpenCombinedPicker, true)
}

const setContextMenuTitle = (target: SelectionTarget): void => {
  contextMenuTitleProxy.value = getSelectionTitle(target)
}

const applySelectionState = (target: SelectionTarget): void => {
  setContextMenuTitle(target)
  focusPickerTarget(target)
}

const setPickerSelection = (target: SelectionTarget): void => {
  applySelectionState(target)
  setPickerTarget(target)
}

const setSelectingMode = (target?: SelectionTarget): void => {
  if (target) {
    setPickerSelection(target)
    return
  }

  if (model.value?.from && !model.value?.to) {
    setPickerSelection(SelectionTarget.TO)
    return
  }

  setPickerSelection(SelectionTarget.FROM)
}

const updateLayout = (): void => {
  if (
    !containerRef.value ||
    !props.splitFields ||
    props.layout !== PDateTimeRangeInputFieldLayout.AUTO
  ) {
    return
  }

  const width = containerRef.value.getBoundingClientRect().width

  isHorizontalLayout.value = width >= 428
}

const showPicker = (target?: SelectionTarget): void => {
  if (props.disable || props.readonly) {
    return
  }

  isOpeningPicker.value = true
  isDateTimePickerVisible.value = true

  nextTick(() => {
    setSelectingMode(target)
    setTimeout(() => {
      isOpeningPicker.value = false
    }, 80)
  })
}

const openPicker = (): void => {
  showPicker()
}

const closePicker = (): void => {
  isDateTimePickerVisible.value = false
}

const validateField = (): void => {
  if (props.splitFields || typeof qFieldComponent.value?.validate !== 'function') {
    return
  }

  void nextTick(() => {
    qFieldComponent.value?.validate()
  })
}

const onTogglePicker = (): void => {
  if (isDateTimePickerVisible.value) {
    closePicker()
    return
  }

  showPicker()
}

const onOpenCombinedPicker = (event: Event): void => {
  const target = event.target
  if (target instanceof Element && target.closest('.ds-p-date-time-range-input-field__clear')) {
    return
  }

  showPicker()
}

const onHandleClickOutside = (event: Event): void => {
  if (isOpeningPicker.value) {
    return
  }

  unfocusSplitFields()

  if (ds.screen().isMobile) {
    return
  }

  const target = event.target as Node | null
  if (!target) {
    return
  }

  if (document.querySelector('.ds-time-picker-context-menu')?.contains(target)) {
    return
  }

  if (document.querySelector('.ds-date-time-picker-context-menu')?.contains(target)) {
    return
  }

  if (document.querySelector('.ds-date-time-range-picker__year-navigation')?.contains(target)) {
    return
  }

  if (document.querySelector('.ds-date-time-range-picker__month-navigation')?.contains(target)) {
    return
  }

  isDateTimePickerVisible.value = false
  validateField()
}

const onConfirmClick = (): void => {
  closePicker()
  validateField()
  emits('confirm')
}

const displayFormat = computed<string>(() => {
  return props.hideTimeField
    ? ds.useFormat().dateTime.NICE_DATE_SHORT
    : ds.useFormat().dateTime.NICE_DATE_TIME_SHORT
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage || fromErrorState.value.hasError || toErrorState.value.hasError
})

const internalErrorMessage = computed<string | undefined>(() => {
  if (props.errorMessage) {
    return props.errorMessage
  }

  if (fromErrorState.value.hasError) {
    return fromErrorState.value.errorMessage
  }

  if (toErrorState.value.hasError) {
    return toErrorState.value.errorMessage
  }

  return undefined
})

const onFromSegmentFocus = (): void => {
  setContextMenuTitle(SelectionTarget.FROM)
  getFormatSegmentBuilderComponent(SelectionTarget.TO)?.deselect()
  setPickerTarget(SelectionTarget.FROM)
}

const onToSegmentFocus = (): void => {
  setContextMenuTitle(SelectionTarget.TO)
  getFormatSegmentBuilderComponent(SelectionTarget.FROM)?.deselect()
  setPickerTarget(SelectionTarget.TO)
}

const onSelectionFrom = (): void => {
  applySelectionState(SelectionTarget.FROM)
}

const onSelectionTo = (): void => {
  applySelectionState(SelectionTarget.TO)
}

onMounted(() => {
  ensureModel()

  resizeObserver.value = new ResizeObserver(() => {
    updateLayout()
  })

  observeContainer(containerRef.value)
  attachCombinedFieldListener()

  updateLayout()

  if (props.splitFields || !props.autofocus) {
    return
  }

  nextTick(() => {
    showPicker()
  })
})

watch(containerRef, (container, previousContainer) => {
  unobserveContainer(previousContainer ?? undefined)
  observeContainer(container ?? undefined)
  updateLayout()
})

watch(
  () => props.splitFields,
  () => {
    detachCombinedFieldListener()

    nextTick(() => {
      attachCombinedFieldListener()
      updateLayout()
    })
  },
)

watch(
  () => [props.layout, props.splitFields],
  () => {
    nextTick(() => {
      updateLayout()
    })
  },
)

onUnmounted(() => {
  detachCombinedFieldListener()
  resizeObserver.value?.disconnect()
})

onClickOutside(rootRef, onHandleClickOutside)

defineExpose({
  togglePicker: onTogglePicker,
  openPicker,
  closePicker,
})
</script>

<template>
  <div ref="rootRef">
    <template v-if="!splitFields">
      <div :data-cy="cy">
        <label-input-field :label="label" :is-required="isRequired" :tooltip="tooltip" />

        <q-field
          ref="qFieldComponent"
          v-model="model"
          :bg-color="Color.WHITE"
          :rules="qRules"
          :disable="disable"
          :readonly="readonly"
          :error="hasAnyErrors"
          :error-message="internalErrorMessage"
          outlined
          no-error-icon
          hide-bottom-space
          class="ds-p-date-time-range-input-field"
          :class="{ 'ds-input-field--readonly': readonly }"
        >
          <template #default>
            <div
              class="p-flex p-items-center p-no-wrap p-overflow-hidden ds-p-date-time-range-input-field__value"
            >
              <format-segment-builder
                ref="fromSegmentBuilderComponent"
                v-model="model.from"
                v-model:error-state="fromErrorStateModel"
                :format="displayFormat"
                :hide-time-field="hideTimeField"
                :default-time="defaultTimeFrom"
                :disable-clearable="disableClearable"
                :readonly="readonly"
                selectable
                @focus="onFromSegmentFocus"
              />

              <p-space :mobile="PSpaceSize.XS" />
              <p-text-form-body text="-" />
              <p-space :mobile="PSpaceSize.XS" />

              <format-segment-builder
                ref="toSegmentBuilderComponent"
                v-model="model.to"
                v-model:error-state="toErrorStateModel"
                :format="displayFormat"
                :hide-time-field="hideTimeField"
                :default-time="defaultTimeTo"
                :disable-clearable="disableClearable"
                :readonly="readonly"
                selectable
                @focus="onToSegmentFocus"
              />
            </div>
          </template>

          <template #append>
            <template v-if="!disableClearable && !model.isEmpty() && !readonly">
              <p-icon
                :size="15"
                icon="xmark-large"
                class="ds-p-date-time-range-input-field__clear"
                cursor-pointer
                @click="model.clear()"
              />
            </template>
          </template>
        </q-field>
      </div>
    </template>

    <template v-else>
      <div
        ref="containerRef"
        :data-cy="cy"
        :class="{
          'p-flex p-full-width p-no-wrap': isHorizontalLayoutResolved,
        }"
      >
        <div :class="{ 'p-flex-1': isHorizontalLayoutResolved }">
          <date-time-input-field
            ref="dateTimeInputFieldFromComponent"
            v-model="model.from"
            :label="labelFrom"
            :tooltip="tooltipFrom"
            :cy="`${cy}-from`"
            :hide-time-field="hideTimeField"
            :time-increment="timeIncrement"
            :disable="disable"
            :readonly="readonly"
            :rules="qRulesFrom"
            :min="min"
            :max="max"
            :disable-clearable="disableClearable"
            :include-time-validation="includeTimeValidation"
            :default-time="defaultTimeFrom"
            :autofocus="autofocus"
            @open-picker="openSplitPicker(SelectionTarget.FROM)"
            @clear="clearBoundaryValue(SelectionTarget.FROM)"
          />
        </div>

        <p-space :mobile="PSpaceSize.SM" :vertical="!isHorizontalLayoutResolved" />

        <div :class="{ 'p-flex-1': isHorizontalLayoutResolved }">
          <date-time-input-field
            ref="dateTimeInputFieldToComponent"
            v-model="model.to"
            :label="labelTo"
            :tooltip="tooltipTo"
            :cy="`${cy}-to`"
            :hide-time-field="hideTimeField"
            :time-increment="timeIncrement"
            :disable="disable"
            :readonly="readonly"
            :rules="qRulesTo"
            :min="min"
            :max="max"
            :disable-clearable="disableClearable"
            :include-time-validation="includeTimeValidation"
            :default-time="defaultTimeTo"
            @open-picker="openSplitPicker(SelectionTarget.TO)"
            @clear="clearBoundaryValue(SelectionTarget.TO)"
          />
        </div>
      </div>
    </template>

    <date-time-context-menu
      v-model="isDateTimePickerVisible"
      :title="contextMenuTitleProxy"
      :persistent="ds.screen().isLaptopAndLarge"
      :grow="!hideTimeField"
      :anchor="anchorPicker"
      :self="selfPicker"
      :offset="offsetPicker"
      cy="date-time-picker-context-menu"
    >
      <p-date-time-range-picker
        ref="dateTimeRangePickerComponent"
        v-model="model"
        :hide-time-field="hideTimeField"
        :time-strict="timeStrict"
        :disable="disable"
        :disable-clearable="disableClearable"
        :time-increment="timeIncrement"
        :fast-choices="fastChoices"
        :min="min"
        :max="max"
        :max-range-days="maxRangeDays"
        :default-time-from="defaultTimeFrom"
        :default-time-to="defaultTimeTo"
        @selecting-from="onSelectionFrom"
        @selecting-to="onSelectionTo"
      />

      <p-space :mobile="PSpaceSize.XXL" :laptop="PSpaceSize.SM" vertical />

      <p-card class="p-flex p-items-center p-no-wrap">
        <p-space :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.GROW" />

        <p-btn
          :text="$t('Close')"
          :type="PBtnType.TERTIARY"
          :grow="ds.screen().isMobile"
          cy="ds-date-time-range-picker-close-btn"
          @click="closePicker"
        />

        <template v-if="!hideConfirmButton">
          <p-space :mobile="PSpaceSize.SM" />

          <p-btn
            :icon-end="ds.screen().isMobile ? null : 'check'"
            :text="$t('Confirm')"
            :grow="ds.screen().isMobile"
            cy="ds-date-time-range-picker-confirm-btn"
            @click="onConfirmClick"
          />
        </template>
      </p-card>
    </date-time-context-menu>
  </div>
</template>
